import React, { useMemo } from 'react';
import { Typography, Container, Grid, Card, CardContent, CardMedia, Box, Divider } from '@mui/material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet'; // Make sure to install this package
import AppAppBar from '../../components/AppAppBar';
import Footer from '../../components/Footer';

// Utility function to convert filename to URL slug
export const fileNameToSlug = (fileName) => {
    return fileName
        .replace('.js', '')
        .replace(/([A-Z])([A-Z0-9])/g, '$1-$2')
        .replace(/([a-z])([A-Z0-9])/g, '$1-$2')
        .replace(/([0-9]+)/g, '-$1-')
        .toLowerCase()
        .replace(/^-+|-+$/g, '')
        .replace(/-+/g, '-');
};

// Globally accessible function to get all blogs
export const getAllBlogs = () => {
    const blogPosts = [];
    // Non-recursive on purpose: authoring scaffolds live in ./_templates/ and must stay
    // out of the index, the router, and the sitemap. A subdirectory keeps them invisible.
    const context = require.context('./', false, /\.js$/);

    context.keys().forEach((path) => {
        if (path === './index.js') return;

        try {
            const module = context(path);
            const Component = module.default;

            if (Component && typeof Component === 'function') {
                const blogData = Component().props;

                if (blogData) {
                    // Convert filename to URL slug
                    const slug = fileNameToSlug(path.replace('./', ''));
                    blogPosts.push({
                        title: blogData.title,
                        description: blogData.meta?.description,
                        image: blogData.heroImage,
                        url: `/blogs/${slug}`,
                        date: blogData.date,
                        author: blogData.author,
                        keywords: blogData.meta?.keywords || [],
                        category: blogData.category || "General"
                    });
                }
            }
        } catch (error) {
            console.warn(`Could not load blog data from ${path}:`, error);
        }
    });

    return blogPosts.sort((a, b) => new Date(b.date) - new Date(a.date));
};

// Function to get blog categories for filtering
const getBlogCategories = (blogs) => {
    const categories = {};

    blogs.forEach(blog => {
        const category = blog.category || "General";
        if (!categories[category]) {
            categories[category] = 0;
        }
        categories[category]++;
    });

    return Object.entries(categories).map(([name, count]) => ({
        name,
        count
    })).sort((a, b) => b.count - a.count);
};

const BlogIndex = () => {
    // Use the function directly to get the blogs
    const blogs = useMemo(() => getAllBlogs(), []);

    // Get categories for potential filtering
    const categories = useMemo(() => getBlogCategories(blogs), [blogs]);

    // Website base URL for canonical links
    const baseUrl = "https://marter.app"; // Replace with your actual domain

    return (
        <>
            {/* SEO Metadata */}
            <Helmet>
                <title>Walmart Seller Insights | Marter Blog</title>
                <meta name="description" content="Expert strategies and actionable insights to help you succeed on Walmart Marketplace. Discover tips, guides, and tutorials for Walmart sellers." />
                <meta name="keywords" content="Walmart, Walmart Marketplace, seller strategies, e-commerce, selling tips, product listing, Walmart SEO" />
                <link rel="canonical" href={`${baseUrl}/blogs`} />

                {/* Open Graph Tags */}
                <meta property="og:title" content="Walmart Seller Insights | Marter Blog" />
                <meta property="og:description" content="Expert strategies and actionable insights to help you succeed on Walmart Marketplace." />
                <meta property="og:url" content={`${baseUrl}/blogs`} />
                <meta property="og:type" content="website" />
                <meta property="og:image" content={`${baseUrl}/images/blog-og-image.jpg`} />
                <meta property="og:site_name" content="Marter - Walmart Seller Tool" />

                {/* Twitter Card Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Walmart Seller Insights | Marter Blog" />
                <meta name="twitter:description" content="Expert strategies and actionable insights to help you succeed on Walmart Marketplace." />
                <meta name="twitter:image" content={`${baseUrl}/images/blog-og-image.jpg`} />

                {/* Structured Data for SEO */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Blog",
                        "name": "Walmart Seller Insights",
                        "description": "Expert strategies and actionable insights to help you succeed on Walmart Marketplace",
                        "url": `${baseUrl}/blogs`,
                        "publisher": {
                            "@type": "Organization",
                            "name": "Marter",
                            "logo": {
                                "@type": "ImageObject",
                                "url": `${baseUrl}/fulllogot.png`
                            }
                        },
                        "blogPost": blogs.map(blog => ({
                            "@type": "BlogPosting",
                            "headline": blog.title,
                            "description": blog.description,
                            "image": blog.image,
                            "datePublished": blog.date,
                            "author": {
                                "@type": "Person",
                                "name": blog.author
                            },
                            "url": `${baseUrl}${blog.url}`
                        }))
                    })}
                </script>
            </Helmet>

            <AppAppBar />
            <Box sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: '#ffffff', // Changed to white for cleaner look
                pt: { xs: '84px', md: '92px' }
            }}>
                <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, flex: 1 }}>
                    {/* Hero Section */}
                    <Box
                        sx={{
                            mb: { xs: 6, md: 8 },
                            textAlign: 'center',
                            maxWidth: '800px',
                            mx: 'auto'
                        }}
                    >
                        <Typography
                            variant="h1"
                            component="h1"
                            sx={{
                                fontWeight: 800,
                                color: '#1a1a1a',
                                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                                lineHeight: 1.2,
                                mb: 3
                            }}
                        >
                            Walmart Seller Insights
                        </Typography>
                        <Typography
                            variant="h2"
                            sx={{
                                color: 'text.secondary',
                                fontSize: { xs: '1.125rem', md: '1.25rem' },
                                lineHeight: 1.6,
                                maxWidth: '600px',
                                mx: 'auto',
                                mb: 6
                            }}
                        >
                            Expert strategies and actionable insights to help you succeed on Walmart Marketplace
                        </Typography>
                    </Box>

                    {/* Category List - Optional for filtering */}
                    {categories.length > 1 && (
                        <Box sx={{ mb: 5 }}>
                            <Box sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                justifyContent: 'center',
                                gap: 1.5
                            }}>
                                {categories.map((category, index) => (
                                    <Box
                                        key={index}
                                        component="a"
                                        href={`#${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                                        sx={{
                                            px: 2.5,
                                            py: 1,
                                            backgroundColor: 'rgba(255, 204, 28, 0.1)',
                                            border: '1px solid rgba(255, 204, 28, 0.3)',
                                            borderRadius: '20px',
                                            color: '#1a1a1a',
                                            fontWeight: 600,
                                            fontSize: '0.875rem',
                                            textDecoration: 'none',
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255, 204, 28, 0.2)',
                                                transform: 'translateY(-2px)',
                                            }
                                        }}
                                    >
                                        {category.name} ({category.count})
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Blog Grid */}
                    <Grid container spacing={{ xs: 2, md: 4 }} sx={{ mb: 6 }}>
                        {blogs.map((blog, index) => (
                            <Grid item xs={12} md={6} lg={4} key={index}>
                                <Link to={blog.url} style={{ textDecoration: 'none' }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                                    <Card sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        transition: 'all 0.3s ease',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        boxShadow: 'none',
                                        border: '1px solid #eaeaea',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 12px 24px rgba(0,0,0,0.06)',
                                            '& .blog-image': {
                                                transform: 'scale(1.05)'
                                            }
                                        }
                                    }}>
                                        <Box sx={{ overflow: 'hidden', position: 'relative' }}>
                                            <CardMedia
                                                component="img"
                                                height="220"
                                                image={blog.image}
                                                alt={blog.title}
                                                className="blog-image"
                                                sx={{
                                                    objectFit: 'cover',
                                                    transition: 'transform 0.6s ease'
                                                }}
                                            />
                                            {/* Category label */}
                                            {blog.category && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 12,
                                                        left: 12,
                                                        px: 1.5,
                                                        py: 0.5,
                                                        borderRadius: '6px',
                                                        backgroundColor: 'rgba(255, 204, 28, 0.9)',
                                                        color: '#1a1a1a',
                                                        fontWeight: 600,
                                                        fontSize: '0.7rem',
                                                        zIndex: 1
                                                    }}
                                                >
                                                    {blog.category}
                                                </Box>
                                            )}
                                        </Box>
                                        <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                            <Typography
                                                variant="h3"
                                                component="h2"
                                                sx={{
                                                    color: '#1a1a1a',
                                                    fontWeight: 700,
                                                    fontSize: '1.25rem',
                                                    lineHeight: 1.4,
                                                    mb: 2,
                                                    minHeight: '2.8em'
                                                }}
                                            >
                                                {blog.title}
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    color: 'text.secondary',
                                                    fontSize: '0.9375rem',
                                                    lineHeight: 1.6,
                                                    mb: 3,
                                                    display: '-webkit-box',
                                                    overflow: 'hidden',
                                                    WebkitBoxOrient: 'vertical',
                                                    WebkitLineClamp: 3
                                                }}
                                            >
                                                {blog.description}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    mt: 'auto',
                                                    pt: 2,
                                                    borderTop: '1px solid #f0f0f0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between'
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    By {blog.author}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: 'text.secondary' }}
                                                >
                                                    {new Date(blog.date).toLocaleDateString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: 'numeric'
                                                    })}
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Link>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
                <Footer />
            </Box>
        </>
    );
};

export default BlogIndex;